import { ColDef, ColGroupDef, PostSortRowsParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import Grid_Excel from "src/components/common/gridWrapper/Grid_Excel";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { defaultColDef, getRowStyleFlags } from "src/utils/grid/gridStyleUtils";
import { createCalendarCellClickHandler, CreateCellClickHandler, CustomCellClickAction, RemoveRowHandler, postSortHandler, useCellChangeHandler, GridCellConfig, createCellCssClassRules } from "src/utils/grid/GridHandlerContent";
import { useDetailTableEvents } from "src/hooks/detail-table/useDetailTableEvents";
import { defaultTQC2070_R01Dto, TQC2070_R01Dto } from "./Dto_TQC2070_R01";
import { useTQC2070_R01Store, useTQC2070_R01Store_GridCalendar } from "./Store_TQC2070_R01";
import { createBaseColumnDefs, createColumnDefs, headerNames } from "./components/ColumnDefinitions";
import { GridCalendar } from "src/components/common/form/GridCalendar";
import { formatDate } from "src/utils/string/stringUtil";
import { cellDateComparator } from "src/utils/grid/gridFunctionUtil";

const COLUMN_STATE_KEY = 'TQC2070_R01-grid-column-state';

interface Props {
	clickable: boolean;
	setClickable: (clickable: boolean) => void;
	gridRef: React.RefObject<AgGridReact<TQC2070_R01Dto> | null>;
	handleInsertClick: (e: React.MouseEvent, rowIndex?: number) => void;
}

export const Table_TQC2070_R01 = (props: Props) => {
	const { clickable, setClickable, gridRef, handleInsertClick } = props;

	const {
		TQC2070_R01List, setTQC2070_R01List,
		insertTQC2070_R01List, removeTQC2070_R01List, updateTQC2070_R01List,
		deletedTQC2070_R01List, addDeletedTQC2070_R01List,
		selectedTQC2070_R01List, setSelectedTQC2070_R01List,
		initSelectedTQC2070_R01List, allSelectedTQC2070_R01List,
		originalTQC2070_R01List,
		columnState, setColumnState,
		scrollTop, setScrollTop,
		editTQC2070_R01,
	} = useTQC2070_R01Store();
	const { TQC2070_R01GridCalendarState, setTQC2070_R01GridCalendarState } = useTQC2070_R01Store_GridCalendar();
	const { pickerVisible, pickerPosition, targetRowIndex, targetField, targetDate } = TQC2070_R01GridCalendarState;

	const gridWrapperRef = useRef<HTMLDivElement>(null);

	const {
		getRowId,
		settingRowIndex,
		createRowClassRules,
		createCellClassRules,
		paddedGridData,
		renderIndexCell,
	} = useGridSettingContent({
		gridRef,
		key: 'valueIndex',
		gridData: TQC2070_R01List,
		defaultGridData: defaultTQC2070_R01Dto,
		selectedRow: editTQC2070_R01 ?? undefined,
		originalGridData: originalTQC2070_R01List,
		updateRow: (index: number, data: any) => updateTQC2070_R01List(index, data),
	});

	const cssCellConfig = useMemo<GridCellConfig>(() => ({
		disabledCells: [''],
		insertedDisabledCells: [''],
		commonDisabledCells: [''],
		
		pointerCells: ['analysisDt', 'znAm', 'znPm', 'naohAm', 'naohPm', 'znInputQty', 'naohInputQty', 'znReanalysisValue', 'naohReanalysisValue', 'remark'],
		insertedPointerCells: [''],
		commonPointerCells: [''],
		
		alignLeftCells: ['']
	}), []);

	const {
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
	} = useMemo(() => createCellCssClassRules(cssCellConfig), [cssCellConfig]);

	const cellCssDefaultColDef = useMemo(() => {
		const baseRules = createCellClassRules<TQC2070_R01Dto>({
			idKey: 'valueIndex',
			selectedList: selectedTQC2070_R01List,
			deletedList: deletedTQC2070_R01List,
			disabledInsertedColumns, disabledColumns,
			pointerInsertedColumns, pointerColumns,
			alignLeftColumns,
		});
		return { ...defaultColDef, cellClassRules: { ...baseRules, }}; 
	}, [
		
		selectedTQC2070_R01List, deletedTQC2070_R01List,
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns,
		alignLeftColumns, createCellClassRules,
	]);

	// 셀 값 변경 핸들러
	const handleCellValueChanged = useCellChangeHandler<TQC2070_R01Dto>({
		dataList: TQC2070_R01List,
		originalDataList: originalTQC2070_R01List,

		updateRow: (valueIndex: string | number, updatedData: TQC2070_R01Dto) => {
			updateTQC2070_R01List(Number(valueIndex), updatedData);
		},

	});
	
	// 편집 가능한 컬럼 목록
	const defaultEditableColumns = ['analysisDt', 'znAm', 'znPm', 'naohAm', 'naohPm', 'znInputQty', 'naohInputQty', 'znReanalysisValue', 'naohReanalysisValue', 'remark'];

	// 순번 컬럼 렌더러 (표준 renderIndexCell 사용)
	const indexCellRenderer = useMemo(() => {
		return (params: any) => {
			return renderIndexCell(
				params,
				(innerParams: TQC2070_R01Dto) => getRowStyleFlags (
					innerParams,
					{
						deletedList: deletedTQC2070_R01List.map(id => id.toString()),
						selectedList: selectedTQC2070_R01List.map(id => id.toString()),
						dataList: TQC2070_R01List
					}),
				(rowIndex: number, e: React.MouseEvent) => {
					if (e.ctrlKey) {
						for (let i = 0; i < 10; i++) { handleInsertClick(e, rowIndex); }
					} else { 
						handleInsertClick(e, rowIndex);
					}
				},
				(rowData: TQC2070_R01Dto) => {
					RemoveRowHandler(
						gridRef,
						TQC2070_R01List,
						rowData,
						setClickable,
						(valueIndex: number) => removeTQC2070_R01List(valueIndex),
						(ids: string[]) => addDeletedTQC2070_R01List(ids.map(id => Number(id))) // string[] → number[] 변환
					);
				}
			);
		};
	}, [renderIndexCell, deletedTQC2070_R01List, selectedTQC2070_R01List, TQC2070_R01List, removeTQC2070_R01List, addDeletedTQC2070_R01List, setClickable, gridRef, handleInsertClick]);

	const baseColumnDefs: (ColDef<TQC2070_R01Dto> | ColGroupDef<TQC2070_R01Dto>)[] = useMemo(
		() => createBaseColumnDefs(indexCellRenderer, cellDateComparator),
		[indexCellRenderer, cellDateComparator]
	);

	const columnDefs: (ColDef<TQC2070_R01Dto> | ColGroupDef<TQC2070_R01Dto>)[] = useMemo(
		() => createColumnDefs(baseColumnDefs, columnState),
		[baseColumnDefs, columnState, settingRowIndex]
	);

	const shiftRangeSelectionConfig = useMemo(() => ({
		enableShiftRangeSelect: true,
		idKey: 'valueIndex' as const,
		selectedIds: selectedTQC2070_R01List as Array<string | number>,
		setSelectedIds: (ids: Array<string | number>) => setSelectedTQC2070_R01List(ids.map((id) => Number(id))),
		isRowSelectable: (event: any) => {
			if (!clickable) {
				setClickable(true);
				return false;
			}
			return !deletedTQC2070_R01List.includes(Number(event.data?.valueIndex));
		},
	}), [selectedTQC2070_R01List, deletedTQC2070_R01List, setSelectedTQC2070_R01List, clickable, setClickable]);

	// 범용 이벤트 훅 사용 - 방향키 이동 기능
	const cellEventHandlers = useDetailTableEvents({
		detailList: TQC2070_R01List as any,
		editableColumns: defaultEditableColumns,
		keyDownConfig: {
			enableArrowNavigation: true,
		}
	});

	// 셀 클릭 액션 설정
	const cellClickActions: CustomCellClickAction<TQC2070_R01Dto>[] = useMemo(() => [
		{
			validate: (field, data) => field === 'analysisDt',
			action: createCalendarCellClickHandler('analysisDt', TQC2070_R01GridCalendarState, setTQC2070_R01GridCalendarState),
		},
		{
			validate: (field, data) => defaultEditableColumns.includes(field),
			// aggrid 기본 편집 기능
			action: (e) => { e.api.startEditingCell({ rowIndex: e.rowIndex!, colKey: e.colDef.field! }); }
		},
	], [
		TQC2070_R01GridCalendarState, defaultEditableColumns, setTQC2070_R01GridCalendarState, createCellClassRules,
	]);
	
	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, []),
	[baseColumnDefs]);

const handleCellClick = CreateCellClickHandler(TQC2070_R01List, 'valueIndex', cellClickActions);

	// 달력 변경 핸들러
	const handleCalendarChange = useCallback((date: Date | undefined, rowIndex: number, targetField: string) => {
		if (rowIndex !== null && targetField) {
			const foundData = TQC2070_R01List[rowIndex];
			if (foundData) {
				updateTQC2070_R01List(foundData.valueIndex, {
					...foundData,
					[targetField]: date ? formatDate(date) : '',
					isModified: true // 날짜 변경 시 수정 상태로 설정
				});
			}
		}
	}, [TQC2070_R01List, updateTQC2070_R01List]);

	// 달력 로드 핸들러
	const handleCalendarLoad = useCallback((rowIndex: number, targetField: string) => {
		const foundData = TQC2070_R01List[rowIndex];
		if (!foundData) return;

		const dateString = foundData[targetField as keyof TQC2070_R01Dto] as string;
		
		if (dateString && dateString !== '') {
			try {
				const date = new Date(dateString);
				if (!isNaN(date.getTime())) {
					setTQC2070_R01GridCalendarState({ targetDate: date });
				} else {
					setTQC2070_R01GridCalendarState({ targetDate: new Date() });
				}
			} catch {
				setTQC2070_R01GridCalendarState({ targetDate: new Date() });
			}
		} else {
			setTQC2070_R01GridCalendarState({ targetDate: new Date() });
		}
	}, [TQC2070_R01List, setTQC2070_R01GridCalendarState]);

	// 정렬 핸들러
	const handlePostSort = useCallback((params: PostSortRowsParams<TQC2070_R01Dto>) => {
		postSortHandler(params, TQC2070_R01List, originalTQC2070_R01List, setTQC2070_R01List);
	}, [TQC2070_R01List, originalTQC2070_R01List, setTQC2070_R01List]);

	const pinnedBottomRow = useMemo(() => ([{
		rowIndex: `${TQC2070_R01List.length.toLocaleString()}건`,
		znAm: "",
		znPm: "",
		naohAm: "",
		naohPm: "",
		znInputQty: "",
		naohInputQty: "",
		znReanalysisValue: "",
		naohReanalysisValue: "",
		remark: "",
		editable: false,
	}]), [TQC2070_R01List]);

useEffect(() => {
	initSelectedTQC2070_R01List();
}, []);

	return (
		<GridContextWrapper<TQC2070_R01Dto>
			enableCellCalculation
			gridWrapperRef={gridWrapperRef}
			gridRef={gridRef}
			paddedGridData={paddedGridData}
			setPaddedGridData={setTQC2070_R01List}
			initSelected={initSelectedTQC2070_R01List}
			defaultRowData={defaultTQC2070_R01Dto}
			allSelected={allSelectedTQC2070_R01List}
			baseColumnDefs={columnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={setColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
			<Grid_Excel
				ref={gridRef}
				rowData={paddedGridData}	getRowId={getRowId}
				columnDefs={columnDefs}
				defaultColDef={cellCssDefaultColDef}
				
				rowClassRules={createRowClassRules<TQC2070_R01Dto>({
					idKey: 'valueIndex', // valueIndex 기준으로 선택/삭제 표시 (다른 페이지와 동일한 방식)
					selectedList: selectedTQC2070_R01List,
					deletedList: deletedTQC2070_R01List
				})}
				shiftRangeSelection={shiftRangeSelectionConfig}
				onCellClicked={handleCellClick}
				onCellKeyDown={cellEventHandlers.handleCellKeyDown}
				onCellValueChanged={handleCellValueChanged}
				pinnedBottomRowData={pinnedBottomRow}

				onPostSortRows={handlePostSort}
				columnStateKey={COLUMN_STATE_KEY}
				setColumnState={setColumnState}
				getDefaultColumnState={() => defaultColumnState}
				scrollTop={scrollTop}
				setScrollTop={setScrollTop}
			/>
			<GridCalendar
				rowIndex={targetRowIndex!}
				targetField={targetField!}
				visible={pickerVisible}
				setVisible={(visible) => setTQC2070_R01GridCalendarState({ pickerVisible: visible })}
				value={targetDate}
				pickerPosition={pickerPosition}
				setPickerPosition={(pos) => setTQC2070_R01GridCalendarState({ pickerPosition: pos })}
				onClear={() => {
					if (targetRowIndex !== null && targetField) {
						handleCalendarChange(undefined, targetRowIndex, targetField);
					}
				}}
				onChange={handleCalendarChange}
				onClickLoad={handleCalendarLoad}
			/>
		</GridContextWrapper>
	);
};

